import React, { useState, useEffect } from 'react';
import { X, ExternalLink } from 'react-feather';
import { API_BASE_URL } from '../../config/api';

interface BannerData {
  id: number;
  message: string;
  linkText: string | null;
  linkUrl: string | null;
  dismissible: boolean;
  targetType: 'CLIENT' | 'CONTRACTOR' | null;
}

interface AnnouncementBannerProps {
  userType?: 'client' | 'contractor';
}

const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({ userType }) => {
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/banners/active`);
        const data = await response.json();

        if (data.success && data.banner) {
          const fetchedBanner = data.banner as BannerData;

          // Check if banner targets specific user type
          if (fetchedBanner.targetType) {
            const targetMatch = fetchedBanner.targetType.toLowerCase() === userType;
            if (!targetMatch) {
              setBanner(null);
              setLoading(false);
              return;
            }
          }

          // Check if user has already dismissed this specific banner
          const dismissedKey = `banner-dismissed-${fetchedBanner.id}`;
          const wasDismissed = localStorage.getItem(dismissedKey) === 'true';

          if (wasDismissed && fetchedBanner.dismissible) {
            setBanner(null);
          } else {
            setBanner(fetchedBanner);
          }
        }
      } catch (error) {
        console.error('Failed to fetch announcement banner:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBanner();
  }, [userType]);

  const handleDismiss = () => {
    if (banner) {
      setIsDismissed(true);
      try {
        localStorage.setItem(`banner-dismissed-${banner.id}`, 'true');
      } catch {
        // localStorage not available
      }
    }
  };

  // Don't render anything while loading or if no banner
  if (loading || !banner || isDismissed) {
    return null;
  }

  const isInternalLink = banner.linkUrl && !banner.linkUrl.startsWith('http');

  return (
    <div
      style={{
        backgroundColor: 'var(--color-banner-bg)',
        color: 'var(--color-banner-text)',
        padding: 'var(--space-2) var(--space-4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-3)',
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-medium)',
        position: 'relative'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        <span>{banner.message}</span>
        {banner.linkText && banner.linkUrl && (
          isInternalLink ? (
            <a
              href={banner.linkUrl}
              style={{
                color: 'var(--color-banner-text)',
                textDecoration: 'underline',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                fontWeight: 'var(--font-weight-semibold)'
              }}
            >
              {banner.linkText}
            </a>
          ) : (
            <a
              href={banner.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--color-banner-text)',
                textDecoration: 'underline',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                fontWeight: 'var(--font-weight-semibold)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              {banner.linkText}
              <ExternalLink size={14} />
            </a>
          )
        )}
      </div>

      {banner.dismissible && (
        <button
          onClick={handleDismiss}
          aria-label="Dismiss announcement"
          style={{
            position: 'absolute',
            right: 'var(--space-4)',
            background: 'none',
            border: 'none',
            color: 'var(--color-banner-text)',
            cursor: 'pointer',
            padding: 'var(--space-1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.8,
            transition: 'var(--transition-fast)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.8';
          }}
        >
          <X size={18} />
        </button>
      )}
    </div>
  );
};

export default AnnouncementBanner;
